import React, { useEffect, useState, ChangeEvent } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import useFetch from 'use-http';
import RHFSelect from '../../controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import CreditCardType from 'src/models/AP/CreditCardType';
import DialogList from 'src/dialogs';
import InfoDialog from './InfoDialog';

interface ILoadRawDataFileDialogProps {
    dialogName: string;
    handleCategories?: (category: string, subcategory: string) => void;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    setFileToUpload?: (formData: FormData) => void;
    creditCardTypes: CreditCardType[];
    onUploaded?: (statementId: number) => void;
}

// Mirrors Oracle Forms' Rawload block: Charged By is only required for CC_NO 60/70 (Chase
// Southwest and one other card) - the WHEN-BUTTON-PRESSED [oid 617] guard, not a blanket rule.
const CREDIT_CARD_TYPES_REQUIRING_CHARGED_BY = ['60', '70'];

const LoadRawDataFileDialog = (props: ILoadRawDataFileDialogProps) => {
    const { register, errors } = useForm();
    const [selectedFile, setSelectedFile] = useState<File>(undefined);
    const { get, post, response } = useFetch();
    const [chargeIndividuals, setChargeIndividuals] = useState<{id: number, name: string}[]>([]);
    const [selectedChargeIndividual, setSelectedChargeIndividual] = useState<string>(undefined);
    const [selectedCreditCard, setSelectedCreditCard] = useState<string>(undefined);
    const [infoMessage, setInfoMessage] = useState<string>('');
    const [infoConfirmLabel, setInfoConfirmLabel] = useState<string>(undefined);
    const [showInfoCancelButton, setShowInfoCancelButton] = useState<boolean>(false);
    const [infoOnConfirm, setInfoOnConfirm] = useState<() => void>(undefined);
    const [infoOnCancel, setInfoOnCancel] = useState<() => void>(undefined);

    const requiresChargedBy = CREDIT_CARD_TYPES_REQUIRING_CHARGED_BY.includes(selectedCreditCard);

    // Replaces window.alert/window.confirm - this dialog and LoadRawDataFileDialog share the
    // single global "active dialog" slot, so showing one hides the other. Cancelling a plain
    // alert or a confirm returns to this dialog so the user can adjust and retry.
    const showAlert = (message: string) => {
        setInfoMessage(message);
        setInfoConfirmLabel(undefined);
        setShowInfoCancelButton(false);
        setInfoOnConfirm(() => () => props.showDialog(DialogList.LOAD_RAW_DATA_FILE));
        setInfoOnCancel(undefined);
        props.hideDialog();
        props.showDialog(DialogList.INFO);
    };

    const showUploadConfirm = (message: string, onConfirm: () => void) => {
        setInfoMessage(message);
        setInfoConfirmLabel('Upload Anyway');
        setShowInfoCancelButton(true);
        setInfoOnConfirm(() => onConfirm);
        setInfoOnCancel(() => () => props.showDialog(DialogList.LOAD_RAW_DATA_FILE));
        props.hideDialog();
        props.showDialog(DialogList.INFO);
    };

    useEffect(() => {
        onCreate();
    }, []);

    const onCreate = () => {
        console.log('LoadRawDataFileDialog onCreate()');
    };

    const onDestroy = () => {
        setSelectedFile(undefined);
        setChargeIndividuals([]);
        setSelectedChargeIndividual(undefined);
        setSelectedCreditCard(undefined);
    };

    const getAuthorizedCreditCardChargeIndividuals = async (ccId: string) => {
        setSelectedCreditCard(ccId);
        let result = await get(`/v1.0/AccountsPayable/creditcard/chargeIndividuals/${ccId}`);

        if (response.ok) {
            setChargeIndividuals(result);
        } else {
            console.log('Unable to get credit card receipt Data!');
        }
    };

    const OKClicked = async () => {
        if (!selectedCreditCard) {
            let ccIdBox = document.getElementById(`!creditCardTypes!`);
            ccIdBox.setAttribute('style', 'width: 100%; border: solid 2px red');
            return;
        } else {
            let ccIdBox = document.getElementById(`!creditCardTypes!`);
            ccIdBox.setAttribute('style', 'width: 100%');
        }
        if (requiresChargedBy && !selectedChargeIndividual) {
            let ccIdBox = document.getElementById(`!chargeIndividuals!`);
            ccIdBox.setAttribute('style', 'width: 100%; border: solid 2px red');
            return;
        } else {
            let ccIdBox = document.getElementById(`!chargeIndividuals!`);
            ccIdBox.setAttribute('style', 'width: 100%');
        }
        if (!selectedFile) {
            let ccIdBox = document.getElementById(`!creditCardStatmentFile!`);
            ccIdBox.setAttribute('style', 'width: 100%; margin: 0px; border: solid 2px red');
            return;
        } else {
            let ccIdBox = document.getElementById(`!creditCardStatmentFile!`);
            ccIdBox.setAttribute('style', 'width: 100%; margin: 0px;');
        }

        await submitUpload(false);
    };

    const submitUpload = async (confirmed: boolean) => {
        const formData = new FormData();
        formData.append('File', selectedFile);
        formData.append('CreditCardType', selectedCreditCard);
        formData.append('Confirmed', confirmed ? 'true' : 'false');
        if (selectedChargeIndividual) {
            formData.append('ChargedBy', selectedChargeIndividual);
        }

        const result = await post('/v1.0/AccountsPayable/creditcardstatements/upload', formData);

        if (!response.ok) {
            showAlert(typeof result === 'string' ? result : 'Unable to upload the credit card statement file.');
            return;
        }

        // Neither this procedure nor the original Oracle Forms LoadRawData guards against
        // re-uploading the same file - it only ever guards against double-converting a
        // statement. Warn on an overlapping date range for the same card and let the user
        // decide, rather than silently double-booking these charges.
        if (result?.hasOverlap) {
            const ranges = (result.overlappingStatements ?? [])
                .map((s: { statementId: number; beginDate: string; endDate: string }) =>
                    `Statement #${s.statementId} (${new Date(s.beginDate).toLocaleDateString()} - ${new Date(s.endDate).toLocaleDateString()})`)
                .join(', ');
            showUploadConfirm(
                `This file's dates overlap an existing statement for this credit card: ${ranges}. Upload anyway?`,
                () => submitUpload(true)
            );

            return;
        }

        if (result?.statementId) {
            onDestroy();
            // onUploaded re-shows the Statement dialog itself (a single showDialog call, since
            // only one dialog can be active at a time) - no separate hideDialog() here, so there's
            // no intermediate "nothing shown" state between this dialog closing and that one reopening.
            props.onUploaded?.(result.statementId);
        } else {
            showAlert('Unable to upload the credit card statement file.');
        }
    };

    const handleCsvFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
        // Nudge with a red border if the credit card / charged by fields aren't filled in yet,
        // but always capture the chosen file regardless - otherwise the native file input shows
        // a filename that was never actually saved to state, and OK later red-outlines the
        // filename box as if no file had been chosen at all.
        if (!selectedCreditCard) {
            let ccIdBox = document.getElementById(`!creditCardTypes!`);
            ccIdBox.setAttribute('style', 'width: 100%; border: solid 2px red');
        } else {
            let ccIdBox = document.getElementById(`!creditCardTypes!`);
            ccIdBox.setAttribute('style', 'width: 100%;');
        }
        if (requiresChargedBy && !selectedChargeIndividual) {
            let ccIdBox = document.getElementById(`!chargeIndividuals!`);
            ccIdBox.setAttribute('style', 'width: 100%; border: solid 2px red');
        } else {
            let ccIdBox = document.getElementById(`!chargeIndividuals!`);
            ccIdBox.setAttribute('style', 'width: 100%;');
        }

        let sfBox = document.getElementById(`!creditCardStatmentFile!`);
        sfBox.setAttribute('style', 'width: 100%; margin: 0px;');
        const fileList = event.currentTarget.files;
        setSelectedFile(fileList[0]);
    };

    return (
        <>
        <Dialog title="Load Raw Data File" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '25%' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <form>
                    <RHFSelect
                        name={`creditCardNumber`}
                        label="Credit Card No"
                        id={`!creditCardTypes!`}
                        labelOnLeft={true}
                        labelStyle={{ width: '20rem', margin: '0'}}
                        options={props?.creditCardTypes?.map(c => {
                            return { value: c.id, label: c.name };
                            })}
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '30rem' }}
                        onChange={(ev) => getAuthorizedCreditCardChargeIndividuals(ev.currentTarget.value)}
                    />
                    <RHFInputBox
                        id={`!creditCardStatmentFile!`}
                        label="Filename"
                        labelOnLeft={true}
                        labelStyle={{ width: '17rem', margin: '0'}}
                        name={`excelFile.filename`}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCsvFileSelected(e)}
                        type="file"
                        register={register}
                        errors={errors}
                        style={{ width: '100%', margin: '0' }}
                        bodyStyle={{ width: '40rem' }}
                    />
                    <RHFSelect
                        id={`!chargeIndividuals!`}
                        name={`ChargedBy`}
                        label={`Charged By${requiresChargedBy ? '' : ' (optional)'}`}
                        labelOnLeft={true}
                        labelStyle={{ width: '20rem', margin: '0'}}
                        options={chargeIndividuals?.map(c => {
                            return { value: c.id, label: c.name };
                            })}
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '30rem', marginTop: '1rem' }}
                        onChange={(ev) => setSelectedChargeIndividual(ev.currentTarget.value)}
                    />
                </form>
            </div>
            <div className="form-row" style={{marginTop: '2rem', justifyContent: 'center'}}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'OK'}
                    onClick={() => OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => props.hideDialog()}
                />
            </div>
                </Dialog>
        <InfoDialog
            dialogName={DialogList.INFO}
            hideDialog={props.hideDialog}
            message={infoMessage}
            confirmLabel={infoConfirmLabel}
            showCancelButton={showInfoCancelButton}
            handleConfirm={infoOnConfirm}
            handleCancellation={infoOnCancel}
        />
        </>
    );
};

export default LoadRawDataFileDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/LoadRawDataFileDialog.tsx